import { leagueFetch } from '@leagueplatform/league-fetch';
import { captureAppError } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';
import { ApiGetData, ApiPatchData } from 'models/profile-settings.model';

const COMM_PREFS_API = '/v1/communication-preferences';
const { OWNERSHIP_TEAM } = SENTRY_CONSTANTS;

/**
 * Fetch available communication preferences for the user.
 * @returns The communication preferences data.
 * @throws Error if the request fails.
 */
export const fetchCommunicationPreferences = async (): Promise<ApiGetData> => {
  const response = await leagueFetch(COMM_PREFS_API, {
    method: 'GET',
    errorContext: {
      ownershipTeam: OWNERSHIP_TEAM,
    },
  });

  if (!response.ok) {
    const error: any = new Error(
      `Request failed with status ${response.status}`,
    );
    error.status = response.status;

    throw error;
  }

  const data = await response.json();
  return data as ApiGetData;
};

/**
 * Update user's communication preferences.
 * @param payload The full communication preferences payload to be sent to the API.
 * @returns `true` if successful, otherwise `false`.
 */
export const updateCommunicationPreferences = async (
  payload: ApiPatchData,
): Promise<unknown> => {
  const response = await leagueFetch(COMM_PREFS_API, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    errorContext: {
      ownershipTeam: OWNERSHIP_TEAM,
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = `Failed to update communication preferences: Status ${
      response.status
    }, Response: ${JSON.stringify(errorData)}`;
    const error: Error & { status?: number } = new Error(errorMessage);
    error.status = response.status;
    captureAppError(error, {
      ownershipTeam: OWNERSHIP_TEAM,
      context: { response: errorData },
    });
    throw error;
  }

  return response.json();
};
